/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Modal, Text } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { showSuccess } from "~/components/Toast";
import {
  generateActionConfirmText,
  generateActionSuccessText,
  useAxios,
} from "~/tools";
import { Customer } from "../BasicCustomerInfo/types";

type DeleteCustomerProps = {
  customer: Customer;
};

function DeleteCustomer({ customer }: DeleteCustomerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isDisabled, setIsDisabled] = React.useState(true);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const history = useHistory();
  const handleDelete = async () => {
    try {
      await Axios().delete(`/customers/${customer?.id}`);
      setIsOpen(true);
      history.push(`/`);
      showSuccess(
        generateActionSuccessText(`The ${customer?.name}`)()("deleted")()
      );
    } catch (e) {
      showError(e.response);
    }
    setIsOpen(false);
  };

  const handleCancel = async () => {
    setIsOpen(false);
  };

  return (
    <>
      <div>
        <Button severity="high" onClick={() => setIsOpen(true)}>
          Delete Customer
        </Button>
      </div>
      <Modal
        contentLabel={generateActionConfirmText(`${customer.name}`)("delete")()}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        actions={[
          {
            order: 0,
            onAction: handleCancel,
            label: "Cancel",
            severity: "medium",
          },
          {
            order: 1,
            onAction: handleDelete,
            label: "Delete",
            severity: "high",
            disabled: isDisabled,
          },
        ]}
      >
        <Text color="danger">
          Removing {customer.name} will lose all customer related information.{" "}
          <br />
          The customer{" "}
          <Text color="danger" tag="span" weight="bold">
            will remain
          </Text>{" "}
          in Klarity.
        </Text>
        To confirm you action please type &apos;delete&apos; below.
        <Input
          placeholder="delete"
          onChange={(e) => setIsDisabled(e.target.value !== "delete")}
        />
      </Modal>
    </>
  );
}

export { DeleteCustomer };
